import { Responsive, Core } from 'connex-cds';
import { DotWorkflowDesktop } from './DotWorkflowDesktop';
import { DotWorkflowMobile } from './DotWorkflowMobile';
import { useParams } from 'react-router';
import { mobileTicket } from '../../../../../../../api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTicketContext } from '../../../TicketContext';
import { useCompanySetup } from '../../../MasterDataProvider';
import { DEFAULT_COC_SETUP_TYPE } from '../../../cocSetupDefaults';

/** Shared so an imperative sync can fetch the same entry this hook observes. */
export const cocSetupQueryOptions = ({ entityRef, ticketRef, cocSetupType } = {}) => ({
  queryKey: ['setup', entityRef, 'coc-setup', ticketRef, cocSetupType],
  queryFn: () => mobileTicket.getCocSetup({ entityRef, ticketRef, queryParams: { coc_setup: cocSetupType } }),
});

export const useGetCocSetup = () => {
  const { entityRef } = useParams();
  const { ticket } = useTicketContext();
  const ticketRef = ticket?.crn;
  const queryClient = useQueryClient();

  const companySetup = useCompanySetup();
  const cocSetupType = companySetup?.data?.batchMaterialSummaryTable ?? DEFAULT_COC_SETUP_TYPE;

  const { queryKey: cocSetupQueryKey, queryFn } = React.useMemo(
    () => cocSetupQueryOptions({ entityRef, ticketRef, cocSetupType }),
    [cocSetupType, entityRef, ticketRef]
  );

  const cachedCocSetup = React.useMemo(() => {
    if (!entityRef || !ticketRef) {
      return null;
    }

    return queryClient?.getQueryData?.(cocSetupQueryKey) ?? null;
  }, [cocSetupQueryKey, entityRef, queryClient, ticketRef]);

  const query = useQuery({
    queryKey: cocSetupQueryKey,
    queryFn,
    staleTime: Infinity,
    // Batch target/actual quantities can sync hours after a ticket is first loaded, so a pre-batch
    // (empty) response must not stay cached for the whole session. Content-based QR has no stored
    // summary to refresh, so this is the only thing keeping its compiled string current when the
    // delivery drawer is reopened.
    refetchOnMount: 'always',
    enabled: !!entityRef && !!ticketRef && !companySetup?.isLoading,
    placeholderData: cachedCocSetup ?? undefined,
  });

  return {
    ...query,
    data: query?.data ?? cachedCocSetup,
  };
};

export const DotWorkflow = () => {
  const cocSetup = useGetCocSetup();
  const { ticket, order } = useTicketContext();

  const cocData = React.useMemo(() => {
    if (!cocSetup?.isLoading) {
      return cocSetup?.data;
    }

    return null;
  }, [cocSetup?.isLoading, cocSetup?.data]);

  const filtereData = React.useMemo(() => {
    return {
      batch: cocData?.setup?.filter?.(_setup => _setup?.isBatchData) ?? [],
      consumption: cocData?.setup?.filter?.(_setup => !_setup?.isBatchData) ?? [],
    };
  }, [cocData]);

  if (cocSetup?.isLoading) return <Core.Spinner spin={cocSetup?.isLoading} />;

  return (
    <Responsive>
      <DotWorkflowMobile data={cocData} ticket={ticket} order={order} filtereData={filtereData} />
      <DotWorkflowDesktop data={cocData} ticket={ticket} order={order} filtereData={filtereData} />
    </Responsive>
  );
};
